import { useEffect, useState } from 'react';

type AttachmentTextState =
  | { status: 'loading' }
  | { status: 'error'; error: Error }
  | { status: 'ready'; text: string };

/** Read an attachment's raw text off an already-viewable URL (signed S3, blob or data). The URL is
 * not a Commenda endpoint, so this is a plain fetch rather than an SWR-cached request: the body can
 * be large and is only ever read once per open preview. */
export function useAttachmentTextContent(url: string): {
  text: string | undefined;
  isLoading: boolean;
  error: Error | undefined;
} {
  const [state, setState] = useState<AttachmentTextState>({
    status: 'loading',
  });

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`status ${res.status}`);
        return res.text();
      })
      .then((text) => {
        if (active) setState({ status: 'ready', text });
      })
      .catch((error: unknown) => {
        if (!active) return;
        setState({
          status: 'error',
          error: error instanceof Error ? error : new Error(String(error)),
        });
      });
    return () => {
      active = false;
    };
  }, [url]);

  return {
    text: state.status === 'ready' ? state.text : undefined,
    isLoading: state.status === 'loading',
    error: state.status === 'error' ? state.error : undefined,
  };
}
